import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import type { CustomerBatchMonthlyBillingInput } from '@super-mega/server/contracts/customer'
import { useTRPC } from '@/trpc/react.ts'
import { CustomerMonthlyBillingFields } from './customer-monthly-billing-fields.tsx'

type Values = Omit<CustomerBatchMonthlyBillingInput, 'ids'>

export function CustomerMonthlyBillingModal({
  ids,
  onClose,
  onSaved
}: {
  ids: number[]
  onClose: () => void
  onSaved: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const mutation = useMutation(trpc.customer.batchSetMonthlyBilling.mutationOptions())
  return (
    <ModalForm<Values>
      open={open}
      title="设置自动每月生成账单"
      layout="vertical"
      width="34rem"
      disabled={mutation.isPending}
      initialValues={{ autoCollectionBillGrouping: false, autoSendCollectionBillGroup: false }}
      modalProps={{
        destroyOnHidden: true,
        mask: { closable: false },
        afterClose: () => {
          if (mutation.isSuccess) onSaved()
          else onClose()
        },
        keyboard: !mutation.isPending,
        closable: !mutation.isPending
      }}
      onOpenChange={(nextOpen) => {
        if (!mutation.isPending) setOpen(nextOpen)
      }}
      submitter={{
        searchConfig: { submitText: '保存设置', resetText: '取消' },
        resetButtonProps: { disabled: mutation.isPending },
        submitButtonProps: { loading: mutation.isPending }
      }}
      onFinish={async (values) => {
        if (mutation.isPending) return false
        try {
          const result = await mutation.mutateAsync({
            ...values,
            collectionBillGroupingDay: values.collectionBillGroupingDay ?? null,
            ids
          })
          await Promise.all([
            queryClient.invalidateQueries(trpc.customer.list.queryFilter()),
            queryClient.invalidateQueries(trpc.customer.billingCalendar.queryFilter())
          ])
          void message.success(`已更新 ${result.count} 个客户的自动收款归组设置`)
          setOpen(false)
          return true
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '批量设置失败')
          return false
        }
      }}
    >
      <p className="border-border bg-surface-muted mb-5 rounded-lg border p-3 text-sm">
        将更新已选 <strong>{ids.length}</strong> 个客户的月度账单设置。
      </p>
      <p className="text-content-muted mb-4 text-sm">
        统一替换所选客户的设置；归组日可留空，留空会清除所选客户的归组日。关闭自动生成时关闭自动发送，已有账单保留。
      </p>
      <CustomerMonthlyBillingFields compact disabled={mutation.isPending} />
    </ModalForm>
  )
}
